import { Buffer } from 'buffer'
import { useCallback, useEffect, useRef, useState } from "react"
import { getToken, getTokenExpirationMs, logout } from './auth'
import { cleanup } from './role'
import ConfirmDialog from '../form/ConfirmDialog'
import EventBus from '../utils/EventBus'

const logLocal = (...args) => {
  if (window.location.hostname.includes('localhost')) {
    console.log(...args)
  }
}

let _timeout = undefined
let _interval = undefined

export default function SessionTimeout(props={token: undefined}) {
  const [isExtendSessionDialogVisible, setIsExtendSessionDialogVisible] = useState(false)
  // ref so the setInterval closure always reads the current value without being re-created
  const isDialogVisibleRef = useRef(false)

  const parseJwt = (token) => {
    if(token) {
      return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString())
    } else {
      console.error("Trying to parse a jwt token, but no token was provided.")
      return false
    }
  }

  const clearMonitors = useCallback(() => {
    if (_timeout !== undefined) {
      clearTimeout(_timeout)
      _timeout = undefined 
    }
    if (_interval !== undefined) {
      clearInterval(_interval)
      _interval = undefined
    }
  }, [])

  const login = useCallback(async () => {
    await getToken()
  }, [])

  const extendSession = useCallback(() => {
    clearMonitors()
    setIsExtendSessionDialogVisible(false)
    login()
  }, [clearMonitors, login])

  const _logout = useCallback(async () => {
    clearMonitors()
    setIsExtendSessionDialogVisible(false)
    cleanup()
    logout()
  }, [clearMonitors])

  const createSessionMonitor = useCallback(() => {
    if (_interval !== undefined) clearInterval(_interval)
      
    _interval = setInterval(async () => {
      const jwt = await getToken(true)
      // use access token expiry (expiresOn), not idTokenClaims.exp which may be a long-lived ID token
      const exp = getTokenExpirationMs(jwt)
      if (exp > 0) {
        let warningGracePeriod = 5 * 60 * 1000
        const now = new Date().getTime()
        logLocal(`Session Interval: warning in ${parseInt((exp - warningGracePeriod - now) / 60 / 1000)} minutes`)

        if (now > exp) {
          extendSession()
        } else if (now + warningGracePeriod > exp) {
          if (!isDialogVisibleRef.current) {
            isDialogVisibleRef.current = true
            setIsExtendSessionDialogVisible(true)
            // proactively refresh so navigation during the warning window doesn't 401
            login()
            // fire at actual expiry, not a fixed 5 min that may overshoot it
            _timeout = setTimeout(extendSession, exp - now)
          }
        }
      } else {
        logLocal('Could not extract exp')
        _logout()
      }
    }, 30 * 1000)
  }, [_logout, extendSession, login])

  const initialize = useCallback((_token) => {
    const jwt = parseJwt(_token?.idToken)
    clearMonitors()
    
    if(jwt) {
      let ttl = (jwt.exp - jwt.iat) / 60
      logLocal(`Server Session duration: ${ttl} minutes. Expire at ${new Date(jwt.exp * 1000).toLocaleString()}`)

      createSessionMonitor()

      } else {
        logLocal(`No Token`)
        _logout()
      }
  }, [clearMonitors, createSessionMonitor, _logout])

  const handleToken = useCallback((token) => {
    logLocal(`handleToken`, token)
    initialize(token)
  }, [initialize])

  useEffect(() => {
    isDialogVisibleRef.current = isExtendSessionDialogVisible
  }, [isExtendSessionDialogVisible])

  useEffect(() => {
    // ensure monitoring starts on mount even if no API call has fired the token event yet
    getToken()
  }, [])

  useEffect(() => {
    EventBus.on('token', handleToken)
    return () => {
      EventBus.removeListener('token', handleToken)
    }
  }, [handleToken])

  return (
    <>
    {isExtendSessionDialogVisible && <ConfirmDialog title="Session token expiring soon" text="We haven't fetched a token for you in a while and the current one is expiring, just checking if you are still here." open={isExtendSessionDialogVisible} handleConfirm={_logout} hideDialog={extendSession}  noLabel="Still Working" noButtonVariant={'contained'} yesLabel="Log Out" yesButtonVariant={'outlined'} />}
    </>
  )
}